import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles } from "lucide-react";

const EMPTY = { title:"", description:"", category:"News", image_url:"", cta_text:"", cta_url:"", published:true, featured:false };

export default function AdminCareerUpdates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const load = React.useCallback(()=>api.get("/admin/career-updates").then(r=>setRows(r.data)),[]);
  useEffect(()=>{ load(); },[load]);
  const save = async () => {
    try {
      await api.post("/admin/career-updates", f);
      toast.success("Update published");
      setOpen(false); setF(EMPTY); load();
    } catch(e){ toast.error("Failed"); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/career-updates/${id}`);
      toast.success("Deleted");
      load();
    } catch(e){ toast.error("Failed"); }
  };
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Content</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Homepage Career Updates</h1>
          <p className="text-slate-400 mt-1">Featured updates appear first on the homepage. Only published items are shown publicly.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="cms-create-btn"><Plus className="w-4 h-4 mr-2"/>New Update</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
            <DialogHeader><DialogTitle>Create career update</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-slate-300">Title</Label>
                <Input value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="cms-title"/>
              </div>
              <div><Label className="text-slate-300">Description</Label>
                <Textarea rows={3} value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="cms-desc"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Category</Label>
                  <Input value={f.category} onChange={e=>setF({...f, category:e.target.value})} placeholder="Hiring | Campus | News | Event" className="bg-slate-800 border-slate-700 text-white"/>
                </div>
                <div><Label className="text-slate-300">Image URL</Label>
                  <Input value={f.image_url} onChange={e=>setF({...f, image_url:e.target.value})} placeholder="https://..." className="bg-slate-800 border-slate-700 text-white"/>
                </div>
                <div><Label className="text-slate-300">CTA text</Label>
                  <Input value={f.cta_text} onChange={e=>setF({...f, cta_text:e.target.value})} placeholder="Explore Roles" className="bg-slate-800 border-slate-700 text-white"/>
                </div>
                <div><Label className="text-slate-300">CTA link</Label>
                  <Input value={f.cta_url} onChange={e=>setF({...f, cta_url:e.target.value})} placeholder="/jobs" className="bg-slate-800 border-slate-700 text-white"/>
                </div>
              </div>
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm"><Switch checked={f.featured} onCheckedChange={v=>setF({...f, featured:v})} data-testid="cms-featured"/>Featured on homepage</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={v=>setF({...f, published:v})}/>Published</label>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="cms-save">Save & Publish</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map(u => (
          <Card key={u.id} className="bg-slate-900 border-slate-800 overflow-hidden card-hover" data-testid={`cms-row-${u.id}`}>
            {u.image_url && <div className="aspect-video overflow-hidden"><img alt={u.title} src={u.image_url} className="w-full h-full object-cover"/></div>}
            <div className="p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-slate-700 text-white border-none hover:bg-slate-700">{u.category}</Badge>
                {u.featured && <Badge className="bg-[#F59E0B] text-[#0A2540] border-none hover:bg-[#F59E0B]"><Sparkles className="w-3 h-3 mr-1"/>Featured</Badge>}
                {!u.published && <Badge className="bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-800">Draft</Badge>}
              </div>
              <h3 className="font-display text-lg font-semibold text-white mt-2">{u.title}</h3>
              <p className="text-sm text-slate-400 mt-1 line-clamp-2">{u.description}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <span>{new Date(u.created_at).toLocaleDateString()}</span>
                <Button size="sm" variant="ghost" onClick={()=>remove(u.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`cms-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <div className="md:col-span-2 text-center py-16 text-slate-500 border border-dashed border-slate-800 rounded-lg">No updates yet. Create the first one above.</div>}
      </div>
    </AdminLayout>
  );
}
